import { Captura, Section } from "./comunes";
import { useAuth } from "@/lib/auth";

export default function AyudaCorreos() {
  // La función de IA está gateada por usuario (flag ai_comentarios_enabled);
  // su documentación se oculta para quien no la tiene, igual que el botón.
  const iaHabilitada = useAuth((s) => s.user?.ai_comentarios_enabled);
  // Pedido de Joseph (2026-07-03): esta sección es SOLO para él — aunque
  // otro usuario tenga la IA habilitada, la doc no se le muestra.
  const esDueno = useAuth((s) => s.user?.es_dueno);
  return (
    <>
      <Section id="correo-entrada" title="Correo de entrada — crear casos desde email">
        <p>
          El sistema recibe correos en una sola dirección. Reenvía (Fwd) ahí
          cualquier correo del cliente, o pide que escriban directo a:
        </p>
        <p className="my-2">
          <code className="rounded bg-ink-950 border border-ink-700 px-2 py-1 font-mono text-accent-300">
            casos@arknex.net
          </code>
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            Si el <strong>asunto trae un TE válido</strong> (formato{" "}
            <code>TE-YYMMDD-NNNNNN</code>) de un caso existente, el correo se
            pega solo al hilo del caso y <strong>se avisa al asignado</strong>{" "}
            (push/Telegram según sus preferencias).
          </li>
          <li>
            Si no trae TE pero el asunto trae un <strong>IMEI</strong> con un
            único caso abierto, también matchea solo.
          </li>
          <li>
            Si nada coincide, cae en la <strong>Bandeja</strong> compartida,
            donde se asigna a un caso o se descarta. Los correos del Google
            Form de Garantías se detectan y permiten crear el caso pre-poblado.
          </li>
          <li>
            <strong>Asignar a caso</strong>: el buscador de esa ventana consulta{" "}
            <strong>todos los casos</strong> —abiertos y cerrados, sin importar
            qué tan viejos sean— por cliente, contacto, folio, TE, ticket JIMI,
            IMEI, equipo o descripción, sin distinguir acentos. Al abrirla verás
            los más recientes; escribe para buscar en el resto.
          </li>
        </ul>
        <Captura
          src="/ayuda-img/bandeja.png"
          alt="Bandeja de correos"
          caption="La Bandeja: correos sin caso, listos para asignar, crear caso o descartar."
        />
        <p className="mt-2 text-zinc-400">
          Para responderle al cliente usa la pestaña <strong>Correos</strong>{" "}
          del caso — si respondes desde tu correo personal, esa respuesta no
          queda registrada en el hilo.
        </p>
      </Section>

      <Section id="destinatarios" title="A quién enviar y a quién saludar">
        <ul className="list-disc list-inside space-y-1">
          <li>
            <strong>Buscador de contactos</strong>: en <strong>Para</strong> o{" "}
            <strong>CC</strong>, el botón <strong>+ Agregar contacto</strong> abre un
            buscador que filtra conforme tecleas y ordena por coincidencia. Busca por
            nombre, correo o empresa entre tus <strong>contactos de proveedores</strong>,
            los <strong>contactos del cliente del caso</strong>, los{" "}
            <strong>asesores comerciales</strong> y tu <strong>equipo interno</strong>{" "}
            (soporte/admin — para poner en copia a un compañero sin teclear su correo) —
            cada resultado trae su etiqueta de origen (incl. «Interno»). Si el correo no
            está guardado, usa el <strong>email libre</strong> de abajo.
          </li>
          <li>
            <strong>Saludar a</strong> (correo al cliente): el saludo del correo
            (&ldquo;Hola [nombre],&rdquo;) sigue al destinatario que pongas en{" "}
            <strong>Para</strong>. Si pones a varios, aparece un selector{" "}
            <strong>Saludar a</strong> para elegir a cuál saludar; los de{" "}
            <strong>CC</strong> nunca se saludan. Así puedes escribirle, por ejemplo,
            al asesor comercial y que el correo lo salude a él.
          </li>
        </ul>
      </Section>

      <Section id="proveedores" title="Seguimiento a proveedores">
        <p>Dos herramientas contra los casos "esperando al proveedor":</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>Resumen diario (automático)</strong>: cada usuario recibe
            por correo y Telegram sus casos que cruzaron el umbral amarillo de
            inactividad, con días sin movimiento, último evento y link directo.
            Sin correo ni Telegram, llega como alerta en la app. Los casos sin
            asignar van con los administradores. Hora y encendido en
            Configuración → Automatizaciones → Avisos programados.
          </li>
          <li>
            <strong>⏰ Recordar al proveedor (manual, un clic)</strong>: cada
            correo enviado tiene un botón que abre un recordatorio cortés
            precargado — mismo hilo, destinatario original, texto editable. Tú
            revisas y envías; nada sale solo.
          </li>
          <li>
            <strong>Contexto del ticket</strong> (modo proveedor): con dos
            switches decides si el correo incluye el{" "}
            <strong>reporte del cliente (Solicitud)</strong> y el{" "}
            <strong>saludo + No. de ticket</strong>. Vienen <strong>apagados</strong>{" "}
            por defecto para que el proveedor se enfoque en lo que tú le pides;
            se recuerda tu preferencia.
          </li>
        </ul>
      </Section>

      <Section id="jimi-abrir" title="Abrir el ticket en Jimi">
        <p>
          El botón <strong>Ticket en Jimi</strong> del caso abre una ventana con
          los datos del caso ya puestos para crear el ticket en el sistema de
          soporte de Jimi: título, equipos, etiqueta de problema y descripción (que
          puedes redactar con IA). Lo revisas, das <strong>Crear en Jimi</strong> y
          el número del ticket queda guardado en el caso.
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>Modelo en Jimi</strong>: Jimi pide elegir el modelo del equipo
            de su catálogo y de ahí saca la <strong>línea de producto</strong>{" "}
            (DVR, rastreador con cable, rastreador inalámbrico…). Viene sugerido:
            primero se busca el modelo exacto con lo que Jimi reporta del IMEI y con
            el modelo del caso; si ninguno está tal cual en su catálogo —el VL110C,
            por ejemplo—, sugiere la familia (VL110). Revísalo antes de crear.
          </li>
          <li>
            <strong>Un solo modelo por ticket</strong>: el sistema de tickets de
            Jimi no acepta modelos revueltos. Si el caso trae equipos de varios
            modelos, la ventana te lo dice en cuanto se abre —cuántos equipos hay de
            cada modelo y cuáles son, con un botón para copiar sus IMEIs— y no deja
            crear el ticket. Separa los equipos en casos de un solo modelo y abre un
            ticket por cada uno.
          </li>
          <li>
            <strong>IMEIs que Jimi no reconoce</strong>: si algún IMEI del caso no
            está registrado en Jimi, también se avisa al abrir la ventana. Corrígelo
            o quítalo del caso.
          </li>
          <li>
            Si en ese momento Jimi no responde, la ventana no puede revisar los
            equipos y lo dice abajo de la lista; la revisión se hace de todos modos al
            darle <strong>Crear en Jimi</strong>, y si algo no cuadra ahí te avisa.
          </li>
          <li>
            <strong>Ya tengo el número</strong>: si el ticket ya lo abriste a mano
            en el portal de Jimi, pega su número (<code>SA-…</code>) y el caso queda
            ligado igual.
          </li>
        </ul>
        <Captura
          src="/ayuda-img/jimi-abrir.png"
          alt="Ventana para abrir el ticket en Jimi con el modelo sugerido"
          caption="El modelo en Jimi viene sugerido y abajo se ve la línea de producto que le corresponde."
        />
      </Section>

      <Section id="jimi" title="Tickets de proveedor — revisar y responder">
        <p>
          Cuando un caso tiene ticket abierto en el portal de Jimi, ya no hace
          falta entrar a ver si contestaron:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>🔄 Revisar en Jimi</strong>: trae al momento lo que el
            técnico haya contestado y lo deja en la{" "}
            <strong>bitácora del caso</strong>, con la fecha que le puso Jimi,
            el equipo del que habla y el <strong>link de los archivos</strong>{" "}
            que haya adjuntado (tutoriales, firmware). Si viene en chino, se
            traduce.
          </li>
          <li>
            <strong>Revisión automática</strong>: cada mañana el sistema lo hace
            solo —de a un ticket por minuto y en desorden, para no saturar a
            Jimi— y le <strong>avisa a quien tenga el caso asignado</strong>.
            Se prende, se apaga y se le cambia la hora en Configuración →
            Integraciones → Tickets de Jimi.
          </li>
          <li>
            <strong>✍️ Responder a Jimi</strong>: contéstale al técnico sin
            salir del caso. Puedes escribir en español (te leen) y, si prefieres,
            el botón <strong>Pasarlo a inglés</strong> lo traduce y tú revisas
            antes de mandar. Lo que envíes queda también en la bitácora.
          </li>
        </ul>
        <p className="mt-2">
          La ventana de responder —y la de abrir ticket en Queclink— traen a la
          mano lo que hace falta para redactar, para no tener que cerrarlas e
          irse a otra pestaña:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>Apuntes del caso</strong>: las notas de la bitácora interna,
            cada una con <strong>+ agregar</strong> para pegarla al mensaje.
          </li>
          <li>
            <strong>📎 Agregar link de evidencias</strong>: pega el link del
            paquete ZIP del caso. Si todavía no hay paquete pero sí evidencias,
            el botón <strong>📦 Generar paquete</strong> lo arma al momento
            —sin contraseña, para que del otro lado no batallen.
          </li>
          <li>
            <strong>Correos del caso</strong>: la lista de los correos que
            entraron y salieron del caso, cada uno con su switch. Lo que{" "}
            <strong>marques</strong> ahí lo <strong>lee la IA</strong> al
            redactar. Arrancan apagados a propósito: tú decides qué material
            entra. Cada renglón dice de quién es, la fecha, el asunto y las
            primeras líneas, así que eliges sin tener que abrirlo — y si el
            correo es un reenvío, te dice quién escribió el mensaje de adentro.
          </li>
          <li>
            <strong>✨ Redactar con IA</strong> (solo si tu usuario la tiene
            habilitada): toma lo que ya escribiste como guía y lo redacta con el
            contexto del caso. Aquí redacta una <strong>respuesta</strong>, no una
            apertura: lee lo que el proveedor escribió en el ticket y le contesta
            sus preguntas, sin volver a presentar el caso desde cero. Siempre lo
            revisas antes de mandarlo.
          </li>
        </ul>
        <p className="mt-2">
          <strong>Por qué marcar un correo cambia el resultado.</strong> La IA
          nunca había visto los correos del caso: solo la bitácora y el hilo del
          ticket. Si el dato que necesitas está en un correo —el reporte que le
          mandó a tu cliente su proveedor de datos, por ejemplo— y no lo marcas,
          la IA no lo tiene… y al pedirle que lo diga, se lo inventa. Marcando el
          correo, escribe lo que de verdad dice el reporte.
        </p>
        <p className="mt-2 text-amber-300/90">
          ⚠️ Una respuesta enviada a Jimi <strong>no se puede borrar ni
          editar</strong> — su sistema no lo permite. Por eso el botón pide
          confirmación antes de mandarla.
        </p>
        <h3 className="font-semibold mt-4 mb-1">
          Los archivos que manda el proveedor
        </h3>
        <p>
          Si Jimi o Queclink adjuntan algo en su ticket —un manual de
          actualización, un log, una foto—, el archivo{" "}
          <strong>se guarda solo en Evidencias</strong> del caso, en su propia
          sección <strong>«📥 Documentos del proveedor»</strong>, con la misma
          retención de 18 meses que cualquier evidencia.
        </p>
        <p className="mt-2">
          Antes solo quedaba la liga dentro de la bitácora: se abría, pero el
          archivo vivía en el servidor del proveedor. El día que lo rotara, la
          respuesta que decía «está en el adjunto» dejaba de llevar a ningún
          lado. Ahora la copia es nuestra.
        </p>
        <p className="mt-2">
          Al armar un <strong>ZIP</strong> esos archivos{" "}
          <strong>no vienen preseleccionados</strong> —devolverle al proveedor
          lo que él mismo mandó no tiene sentido— pero los puedes marcar a mano
          si de verdad los quieres reenviar.
        </p>

        <h3 className="font-semibold mt-4 mb-1">
          La ficha «el proveedor contestó»
        </h3>
        <p>
          Cuando Jimi o Queclink escriben en su ticket, el caso se marca con{" "}
          <em>"⏳ el proveedor contestó"</em> y entra al tablero{" "}
          <strong>Proveedor</strong> de la barra de arriba. Es el recordatorio de
          que <strong>les debemos una respuesta</strong>; convive con el estado
          del caso sin pisarlo, para que no se pierda uno de los dos.
        </p>
        <p className="mt-2">
          La ficha se apaga sola cuando <strong>le respondes en su ticket</strong>{" "}
          o cuando <strong>cierras el caso</strong>. Si se resolvió por otra vía
          —garantía, reemplazo, el cliente desistió— y ya no hace falta
          contestarle, dale clic a la ficha y elige <strong>«Ya lo vi»</strong>:
          se apaga y queda en la bitácora quién lo marcó y por qué. Si el
          proveedor vuelve a escribir, la ficha reaparece sola.
        </p>
        <p className="mt-2">
          Junto al número del ticket, en el encabezado del caso, se ve{" "}
          <strong>en qué estado lo tiene el proveedor</strong> —en Jimi:{" "}
          <em>Recibido, sin atender</em>, <em>En atención</em>,{" "}
          <em>Concluido por Jimi</em> o <em>Suspendido por Jimi</em> (lo pausan con un
          motivo, que el técnico deja escrito en su respuesta y por eso ya está en la
          bitácora); en Queclink: <em>Nuevo</em>, <em>Abierto</em>,{" "}
          <em>Esperan nuestra respuesta</em>, <em>En espera</em>, <em>Resuelto</em>,{" "}
          <em>Cerrado</em>—. Se actualiza en cada revisión (la automática o el botón),
          así que dice lo que había la última vez que se miró, no lo de este segundo.
          En la lista de casos la ficha 🎫 solo dice de qué proveedor es; el estado está
          en el encabezado del caso y en la ficha del tablero «El proveedor contestó».
        </p>
        <Captura
          src="/ayuda-img/jimi-responder.png"
          alt="Ventana para responderle al técnico de Jimi desde el caso"
          caption="La ventana de responder: el mensaje, los apuntes del caso a la mano y el link de evidencias. Se confirma antes de enviar."
        />
        <h3 className="font-semibold mt-4 mb-1">Queclink</h3>
        <p>
          Queclink atiende con <strong>Zendesk</strong>, así que funciona igual:{" "}
          <strong>🔄 Revisar en Queclink</strong> trae sus respuestas a la
          bitácora y <strong>✍️ Responder a Queclink</strong> les contesta desde
          el caso. Dos diferencias:
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>🎫 Abrir ticket en Queclink</strong>: crea el ticket desde el
            caso, con el asunto y la descripción ya armados (los puedes editar).
            Deja el <strong>[TE-…]</strong> en el asunto: es lo que hace que el
            ticket se ligue solo al caso.
          </li>
          <li>
            <strong>🔗 Buscar ticket</strong>: si el ticket ya existía en
            Queclink, lo encuentra por el TE y lo amarra — no hay que capturar
            números.
          </li>
          <li>
            <strong>Ya tengo el número</strong> (dentro de «Ticket en Queclink»):
            para los tickets que se abrieron a mano o desde el correo y no traen
            el TE en el asunto, que «Buscar ticket» no encuentra. Pegas el número
            (el que va en sus correos: «Su solicitud de soporte (38726)»), se
            comprueba que exista en Queclink y queda ligado; en la siguiente
            revisión su hilo entra a la bitácora.
          </li>
          <li>
            <strong>Resuelto y Cerrado no son lo mismo.</strong> Cuando Queclink
            marca el ticket como <em>Resuelto</em>, todavía puedes contestarle: tu
            respuesta lo reabre. Unos días después su sistema lo pasa a{" "}
            <em>Cerrado</em>, y ahí ya no acepta respuestas ni se puede reabrir —
            es regla de Zendesk, no nuestra. Por eso, cuando Queclink resuelve o
            cierra el ticket de un caso abierto, <strong>le llega un aviso al
            asignado</strong>: el de «resuelto» es la última oportunidad de
            contestar en el mismo ticket.
          </li>
          <li>
            <strong>🎫 Ticket de seguimiento</strong>: si el ticket ya está{" "}
            <em>Cerrado</em>, el botón «Responder a Queclink» cambia a este. Abre
            la misma ventana, pero lo que escribas crea un{" "}
            <strong>ticket nuevo en Queclink, ligado al cerrado</strong> y con el
            mismo asunto (con el TE). El caso pasa a seguir el ticket nuevo; lo
            del anterior se queda en la bitácora. Escríbelo para que se entienda
            solo: quien lo tome tal vez no lea el ticket anterior. Si le das
            «Enviar» a un ticket que se cerró mientras escribías, no se manda
            nada: la ventana cambia sola a seguimiento y tu texto se queda ahí.
          </li>
          <li>
            <strong>Se revisa cada hora</strong>, de 7 de la mañana a 8 de la
            noche. Es distinto a Jimi —que se revisa una vez al día, temprano—
            porque Queclink contesta en horario de México y una respuesta de
            media mañana no puede esperar hasta el día siguiente.
          </li>
        </ul>
        <Captura
          src="/ayuda-img/queclink-seguimiento.png"
          alt="Ventana para abrir un ticket de seguimiento en Queclink cuando cerraron el anterior"
          caption="Queclink cerró el ticket: la ventana avisa que se abrirá uno nuevo, ligado al anterior y con el mismo asunto. Se confirma antes de enviar."
        />
        <p className="mt-2">
          Las credenciales de Queclink se guardan <strong>cifradas</strong> en
          Configuración → Integraciones → Tickets de Queclink, con un botón para probar la conexión.
        </p>

        <p className="mt-2">
          En la bitácora se distingue quién escribió cada cosa:{" "}
          <em>"Ticket de Jimi/Queclink creado: …"</em> con el{" "}
          <strong>planteamiento completo</strong> que le mandamos al abrirlo (título y
          descripción, tal cual salieron),{" "}
          <em>"Respuesta de Jimi/Queclink — [técnico]"</em> cuando contestó el proveedor y{" "}
          <em>"Le respondimos a Jimi"</em> cuando fuimos nosotros — aunque se haya
          escrito directo en su portal. Así el hilo completo se lee de corrido sin entrar a
          su portal. Las cuentas del equipo se dan de alta en
          Configuración → Integraciones → Tickets de Jimi.
        </p>
      </Section>

      {iaHabilitada && esDueno && (
      <Section id="comentario-ia" title="Generar comentario con IA">
        <p>
          En el modal de envío, si tu usuario lo tiene habilitado, aparece el botón{" "}
          <strong>✨ Generar comentario con IA</strong>. Redacta un borrador a partir
          de las <strong>notas de la bitácora interna</strong> del caso (esa es la
          fuente de los hechos: no inventa). Con el selector{" "}
          <strong>Redactar para</strong> eliges el destino: <strong>Cliente</strong>,{" "}
          <strong>Proveedor</strong> o <strong>Asesor comercial</strong> — este último
          redacta un correo <strong>interno</strong> para tu compañero de ventas,
          hablándole del cliente y su equipo (lo saluda por su nombre y sí puede
          mencionar el caso/TE).
        </p>
        <p className="mt-2">
          La instrucción se escribe en su <strong>propio recuadro</strong> («Qué
          quieres que diga»), no en el editor del correo. Es a propósito:{" "}
          <strong>eso nunca se manda</strong>. Antes se escribía en la misma caja
          por donde sale el correo y quien le daba a Enviar en vez de ✨ le mandaba
          su propia instrucción al cliente. Si ya traías la idea escrita arriba,{" "}
          <strong>↑ traer lo de arriba</strong> te la copia al recuadro.
        </p>
        <p className="mt-2">
          La instrucción <strong>manda sobre el formato por defecto</strong>, no es
          una sugerencia. La IA ve las notas de la bitácora, los{" "}
          <strong>contactos</strong> y los <strong>equipos</strong> del caso; si le
          pides algo que no está en ese contexto, lo deja marcado como{" "}
          <code>[falta: …]</code> en vez de inventarlo o saltárselo callado. Al{" "}
          <strong>proveedor</strong> no le comparte los datos personales del cliente
          salvo que se lo pidas. El texto queda <strong>100% editable</strong>;
          revísalo — nunca se envía solo.
        </p>
      </Section>
      )}

      <Section id="proveedor-pendiente" title="El proveedor contestó (y falta responderle)">
        <p>
          La app trae solita las respuestas de Jimi y Queclink a la bitácora, pero
          eso por sí solo no basta: el aviso del momento se entierra y la
          respuesta se queda ahí. Para que no se pierdan, un caso queda marcado
          como <strong>pendiente NUESTRO</strong> en cuanto el proveedor escribe, y
          <strong>no se desmarca hasta que alguien le responde</strong> desde el caso.
        </p>
        <p className="mt-2">Lo ves en cuatro lugares, según lo que estés haciendo:</p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            <strong>Contador en la barra de arriba</strong> (y en «Más» del celular):
            el número no se va hasta que se contesta. Es el que te lo recuerda sin
            que lo busques.
          </li>
          <li>
            <strong>Vista «El proveedor contestó»</strong>: la lista completa, los más
            viejos primero, con cliente, ticket, quién lo atiende y los días de
            espera. Amarillo desde 2 días, rojo desde 5. Se abre{" "}
            <strong>tocando cualquier parte del renglón</strong>, igual que en Casos, y
            al entrar el recorrido de «‹ Anterior · Siguiente ›» (y el gesto del dedo)
            pasa a ser <strong>esta</strong> lista: puedes despacharlos de corrido sin
            volver al tablero.
          </li>
          <li>
            <strong>Card en el Panel</strong>: el vistazo de la mañana con el total y
            el más atrasado.
          </li>
          <li>
            <strong>Filtro en Casos</strong> («El proveedor ya contestó») para trabajar
            la tanda de corrido con los demás filtros que uses.
          </li>
        </ul>
        <p className="mt-2 text-zinc-400">
          ⚠️ Los días se cuentan desde que escribió <strong>el proveedor</strong>, no
          desde que la app lo trajo. Importa: si se importa un rezago, todo llega
          «hoy» y un ticket contestado la semana pasada se vería recién llegado.
        </p>
        <p className="mt-2">
          El <strong>resumen de la mañana</strong> (correo y Telegram) también los
          lista, con la liga a la vista.
        </p>
      </Section>

      <Section id="deshacer" title="Deshacer un correo recién enviado">
        <p>
          Al darle <strong>Enviar ahora</strong>, el correo <strong>no sale al
          instante</strong>: se queda un minuto en cola y aparece un aviso{" "}
          <strong>«Correo en camino · Deshacer»</strong>. Si le picas a Deshacer,
          nunca se manda.
        </p>
        <p className="mt-2">
          Si se te va el aviso, todavía alcanzas: el correo se ve en la pestaña{" "}
          <strong>Correos</strong> como programado, con sus botones de{" "}
          <strong>Cancelar</strong>, <strong>Reprogramar</strong> y{" "}
          <strong>Enviar ahora</strong> (por si no quieres esperar el minuto).
        </p>
        <p className="mt-2 text-zinc-400">
          Detalle fino: el sistema revisa la cola cada minuto, así que el correo
          sale entre uno y dos minutos después de darle Enviar. Si el envío falla
          (SMTP mal configurado, por ejemplo), el error se ve en el correo dentro de
          la pestaña <strong>Correos</strong>, marcado en rojo.
        </p>
      </Section>

      <Section id="zip" title="Paquetes ZIP firmados">
        <p>Al enviar evidencias al soporte de la marca, el sistema:</p>
        <ol className="list-decimal list-inside space-y-1 mt-2">
          <li>Empaqueta los archivos en un ZIP cifrado AES-256.</li>
          <li>Contraseña = últimos 6 dígitos del TE del caso.</li>
          <li>Genera un enlace público de descarga que vence a los 90 días.</li>
          <li>Adjunta al correo el enlace y la contraseña.</li>
        </ol>
        <p className="mt-2">
          Al armar el paquete eliges qué archivos entran: la lista trae los{" "}
          <strong>más recientes arriba</strong> y cada renglón muestra la{" "}
          <strong>fecha en que se cargó</strong> (con la hora). Ojo: es la fecha de <em>carga</em>, no la del
          archivo — el nombre que pone WhatsApp trae la fecha en que se grabó,
          que suele ser otra.
        </p>
        <p className="mt-2 text-zinc-400">
          Cada descarga queda registrada (IP, navegador, fecha). Las evidencias
          se conservan 18 meses; los ZIP, 12 meses después del cierre del caso.
        </p>
      </Section>

      <Section id="adjuntar-evidencias" title="Adjuntar evidencias al correo (sin ZIP)">
        <p>
          Al redactar un correo, en la zona de adjuntos tienes{" "}
          <strong>«🖼️ Desde evidencias del caso»</strong>: abre las evidencias
          que ya subiste y adjunta las livianas directo, sin empaquetarlas en un
          ZIP.
        </p>
        <ul className="list-disc list-inside space-y-1 mt-2">
          <li>
            Elegibles: imágenes, PDF y Office/CSV/TXT de{" "}
            <strong>hasta 10&nbsp;MB</strong>.
          </li>
          <li>
            Al elegir una <strong>imagen</strong> puedes{" "}
            <strong>Visualizarla</strong> a tamaño completo (como en Evidencias)
            o <strong>Insertarla</strong>: <em>en el cuerpo</em> del correo o
            como <em>archivo</em> adjunto. Los demás archivos livianos se
            adjuntan directo.
          </li>
          <li>
            Las <strong>pesadas</strong> (&gt;10&nbsp;MB) o de tipo no adjuntable
            (video) aparecen en gris con el motivo: ésas se mandan por{" "}
            <strong>ZIP</strong> (ver arriba).
          </li>
        </ul>
      </Section>
    </>
  );
}
